import React, { useState, useEffect, useRef } from 'react';

const BASE_Z_INDEX = -2000;

const Sprite = React.forwardRef(({
  emoji,
  size = 40,
  style = {},
  className = '',
  ...props
}, ref) => {
  return (
    <div
      ref={ref}
      className={className}
      style={{
        fontSize: `${size}px`,
        lineHeight: 1,
        height: `${size}px`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        ...style
      }}
      {...props}
    >
      {emoji}
    </div>
  );
});

Sprite.displayName = 'Sprite';

const Shadow = ({ width = 20, height = 6 }) => (
  <div
    style={{
      position: 'absolute',
      left: '50%',
      top: '100%',
      width: `${width}px`,
      height: `${height}px`,
      backgroundColor: 'rgba(0,0,0,0.3)',
      borderRadius: '50%',
      transform: 'translate(-50%, -50%)'
    }}
  />
);

function GameStage({ gameState, gameInterface }) {
  const [tick, setTick] = useState(0);
  const [camera, setCamera] = useState({ x: 600, y: 600 });

  useEffect(() => {
    const interval = setInterval(() => {
      setTick(t => t + 1);
    }, 10);
    return () => clearInterval(interval);
  }, []);

  // Update camera to follow current player
  useEffect(() => {
    const currentPlayer = gameState.entities[gameInterface.userId];
    if (currentPlayer) {
      let targetX = currentPlayer.x - gameState.screenSize.width / 2;
      let targetY = currentPlayer.y - gameState.screenSize.height / 2;
      
      // Constrain camera to world bounds
      targetX = Math.max(0, Math.min(targetX, gameState.worldSize.width - gameState.screenSize.width));
      targetY = Math.max(0, Math.min(targetY, gameState.worldSize.height - gameState.screenSize.height));
      
      // Smooth camera movement
      setCamera(prev => ({
        x: prev.x + (targetX - prev.x) * 0.1,
        y: prev.y + (targetY - prev.y) * 0.1
      }));
    }
  }, [tick, gameState.entities, gameInterface.userId]);

  const isInView = (x, y, margin = 100) => {
    const screenX = x - camera.x;
    const screenY = y - camera.y;
    return screenX >= -margin &&
           screenX <= gameState.screenSize.width + margin &&
           screenY >= -margin &&
           screenY <= gameState.screenSize.height + margin;
  };

  return (
    <div className="h-full w-full relative" style={{ transform: 'translateZ(0)' }}>
      <div
        className="h-full w-full relative bg-gradient-to-b from-gray-800 to-gray-900 overflow-hidden"
        style={{ zIndex: BASE_Z_INDEX, userSelect: 'none' }}
      />
      
      {/* Game World Border */}
      <div
        style={{
          position: 'absolute',
          left: -camera.x,
          top: -camera.y,
          width: gameState.worldSize.width,
          height: gameState.worldSize.height,
          border: '4px solid rgba(255,255,255,0.3)',
          zIndex: BASE_Z_INDEX + 1,
        }}
      />
      
      {/* Color Zones */}
      {gameState.colorZones.map((zone) => (
        isInView(zone.x, zone.y, zone.radius) && (
          <div
            key={zone.id}
            style={{
              position: 'absolute',
              left: zone.x - camera.x,
              top: zone.y - camera.y,
              width: zone.radius * 2,
              height: zone.radius * 2,
              backgroundColor: zone.color,
              borderRadius: '50%',
              opacity: zone.active ? 0.7 : 0.2,
              transform: 'translate(-50%, -50%)',
              zIndex: BASE_Z_INDEX + 2,
              transition: 'opacity 0.3s',
              boxShadow: zone.active ? `0 0 20px ${zone.color}` : 'none'
            }}
          />
        )
      ))}

      {/* Player Entities */}
      {Object.values(gameState.entities).map((entity) => (
        isInView(entity.x, entity.y) && (
          <div
            key={entity.id}
            style={{
              position: 'absolute',
              left: entity.x - camera.x,
              top: entity.y - camera.y,
              transform: 'translate(-50%, -50%)',
              zIndex: Math.floor(entity.y) + BASE_Z_INDEX + 10,
              opacity: entity.isAlive ? 1 : 0.5,
            }}
          >
            <div className="flex flex-col items-center gap-1">
              <div style={{ color: 'white', marginBottom: '4px', textAlign: 'center' }}>
                {entity.name}
                <div style={{ fontSize: '12px' }}>
                  Score: {entity.score}/15
                </div>
              </div>
              <div style={{ transform: `scaleX(${entity.direction === 'left' ? -1 : 1})` }}>
                <div
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    backgroundColor: entity.currentColor,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: `0 0 10px ${entity.currentColor}`,
                    border: '2px solid white'
                  }}
                  className={entity.state === 'walking' ? 'animate-pulse' : ''}
                >
                  <span style={{ fontSize: '20px' }}>
                    {entity.isAlive ? '😊' : '😵'}
                  </span>
                </div>
              </div>
              <Shadow />
            </div>
          </div>
        )
      ))}
    </div>
  );
}

export default GameStage;
